import * as React from "react";
import clsx from "clsx";
import { UserPlus } from "lucide-react";
import { useT } from "../../../app/lib/i18n";
import { Button } from "../Button";
import { UserMenu } from "../UserMenu";
import type { UserMenuHandle } from "../UserMenu";
import { OrgLogoStack } from "../../../app/components/organization-context/OrgLogoStack";
import { OrgContextSummary } from "../../../app/components/organization-context/OrgContextSummary";
import { OrgContextPickerBody } from "../../../app/components/organization-context/OrgContextPickerBody";
import * as orgStyles from "../../../app/components/organization-context/OrgContext.css";
import * as s from "./NavigationBar.style.css";
import type { NavigationBarProps, NavLink, NavSection } from "./NavigationBar.types";
import {
  LAYOUT_MOBILE_MENU_TRANSITION_MS,
  useIsLayoutMobile,
} from "../../hooks/useLayoutMobile";

export type {
  NavigationBarProps,
  NavLink,
  NavSection,
  NavSubLink,
  NavProduct,
  NavUser,
  NavOrgContext,
  NavOrgContextMode,
} from "./NavigationBar.types";

function isNavSection(item: NavLink | NavSection): item is NavSection {
  return "children" in item && Array.isArray((item as NavSection).children);
}

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PanelLeftCloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d="M16 15l-3-3 3-3" />
  </svg>
);

type DropdownDir = "down" | "up";

function useDropdown(triggerRef: React.RefObject<HTMLElement | null>, dir: DropdownDir = "down") {
  const [triggerRect, setTriggerRect] = React.useState<DOMRect | null>(null);

  const open = React.useCallback(() => {
    if (!triggerRef.current) return;
    setTriggerRect(triggerRef.current.getBoundingClientRect());
  }, [triggerRef]);

  const close = React.useCallback(() => setTriggerRect(null), []);

  const getStyle = React.useCallback((): React.CSSProperties => {
    if (!triggerRect) return {};
    if (dir === "down") return { top: triggerRect.bottom + 4, left: triggerRect.left };
    return { bottom: window.innerHeight - triggerRect.top + 4, left: triggerRect.left };
  }, [triggerRect, dir]);

  return { isOpen: triggerRect !== null, open, close, getStyle };
}

const DEFAULT_OPEN_WIDTH = 220;
const DEFAULT_CLOSED_WIDTH = 56;

function MobileMenuIcon({ open }: { open: boolean }) {
  return (
    <span className={clsx(s.navMobileMenuIcon, open && s.navMobileMenuIconOpen)} aria-hidden>
      <span className={clsx(s.navMobileMenuLine, s.navMobileMenuLineTop)} />
      <span className={clsx(s.navMobileMenuLine, s.navMobileMenuLineMid)} />
      <span className={clsx(s.navMobileMenuLine, s.navMobileMenuLineBot)} />
    </span>
  );
}

export const NavigationBar = ({
  brand,
  products = [],
  activeProductId,
  onProductChange,
  links = [],
  subLinks = [],
  user,
  orgContext,
  open = true,
  onOpenChange,
  width = DEFAULT_OPEN_WIDTH,
  closedWidth = DEFAULT_CLOSED_WIDTH,
  onWidthChange,
  embedded = false,
  headerTrailing,
}: NavigationBarProps) => {
  const t = useT();
  const isLayoutMobile = useIsLayoutMobile();
  const isMobileNav = isLayoutMobile && !embedded;
  const currentWidth = open ? width : closedWidth;

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [mobileMenuPresent, setMobileMenuPresent] = React.useState(false);
  const [mobileMenuEntered, setMobileMenuEntered] = React.useState(false);
  const [productInlineOpen, setProductInlineOpen] = React.useState(false);

  const productSectionRef = React.useRef<HTMLDivElement>(null);
  const productTriggerRef = React.useRef<HTMLButtonElement>(null);
  const productDropdownRef = React.useRef<HTMLDivElement>(null);
  const userMenuRef = React.useRef<UserMenuHandle>(null);
  const orgContextTriggerRef = React.useRef<HTMLButtonElement>(null);
  const orgContextDropdownRef = React.useRef<HTMLDivElement>(null);

  const productDropdown = useDropdown(productTriggerRef, "down");
  const orgContextDropdown = useDropdown(orgContextTriggerRef, "up");

  const closeMobile = React.useCallback(() => {
    if (!isMobileNav) return;
    setMobileMenuOpen(false);
    setProductInlineOpen(false);
    productDropdown.close();
    userMenuRef.current?.close();
    orgContextDropdown.close();
  }, [isMobileNav, productDropdown, orgContextDropdown]);

  const wrapAction = React.useCallback(
    (fn?: () => void) => () => {
      fn?.();
      closeMobile();
    },
    [closeMobile],
  );

  React.useEffect(() => {
    onWidthChange?.(isMobileNav ? 0 : currentWidth);
  }, [currentWidth, onWidthChange, isMobileNav]);

  React.useEffect(() => {
    if (!isMobileNav) return;

    if (mobileMenuOpen) {
      setMobileMenuPresent(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setMobileMenuEntered(true));
      });
      return () => cancelAnimationFrame(raf);
    }

    setMobileMenuEntered(false);
    const timeout = window.setTimeout(
      () => setMobileMenuPresent(false),
      LAYOUT_MOBILE_MENU_TRANSITION_MS,
    );
    return () => clearTimeout(timeout);
  }, [isMobileNav, mobileMenuOpen]);

  React.useEffect(() => {
    if (!isMobileNav || !mobileMenuPresent) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileNav, mobileMenuPresent]);

  React.useEffect(() => {
    if (!isMobileNav) return;
    setMobileMenuOpen(false);
    setProductInlineOpen(false);
  }, [isLayoutMobile, isMobileNav]);

  React.useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inProduct =
        productSectionRef.current?.contains(target) ||
        productDropdownRef.current?.contains(target);
      const inOrgContext =
        orgContextTriggerRef.current?.contains(target) ||
        orgContextDropdownRef.current?.contains(target);
      if (!inProduct) {
        productDropdown.close();
        if (isMobileNav) setProductInlineOpen(false);
      }
      if (!inOrgContext) orgContextDropdown.close();
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [productDropdown, orgContextDropdown, isMobileNav]);

  const activeProduct = products.find((p) => p.id === activeProductId) ?? products[0];

  const handleProductTriggerDesktop = () => {
    if (productDropdown.isOpen) productDropdown.close();
    else productDropdown.open();
  };

  const handleProductTriggerMobile = () => {
    setProductInlineOpen((v) => !v);
    productDropdown.close();
  };

  const handleOrgContextTrigger = () => {
    if (orgContextDropdown.isOpen) orgContextDropdown.close();
    else orgContextDropdown.open();
    userMenuRef.current?.close();
  };

  const showOrgContext = orgContext != null;

  const toggleMobileMenu = () => {
    setMobileMenuOpen((v) => {
      if (v) setProductInlineOpen(false);
      return !v;
    });
  };

  const renderProductOptions = (afterSelect: () => void) =>
    products.map((product) => {
      const isActive = product.id === activeProductId;
      return (
        <button
          key={product.id}
          type="button"
          role="option"
          aria-selected={isActive}
          className={clsx(s.navProductOption, isActive && s.navProductOptionActive)}
          onClick={() => {
            onProductChange?.(product.id);
            afterSelect();
          }}
        >
          {product.icon ? (
            <span className={s.navProductOptionIcon}>{product.icon}</span>
          ) : (
            <span className={s.navProductOptionIcon}>●</span>
          )}
          <span className={s.navProductOptionLabel}>
            <div>{product.label}</div>
            {product.description && <div className={s.navProductOptionDesc}>{product.description}</div>}
          </span>
          {isActive && (
            <span className={s.navProductCheckmark}>
              <CheckIcon />
            </span>
          )}
        </button>
      );
    });

  const renderProductSection = (opts: { sidebarOpen: boolean; mobile?: boolean }) => {
    const { sidebarOpen, mobile } = opts;
    if (products.length === 0) return null;

    const productOpen = mobile ? productInlineOpen : productDropdown.isOpen;

    return (
      <div ref={productSectionRef} className={s.navProductSection}>
        <button
          ref={productTriggerRef}
          type="button"
          className={s.navProductTrigger}
          onClick={mobile ? handleProductTriggerMobile : handleProductTriggerDesktop}
          aria-expanded={productOpen}
          aria-haspopup="listbox"
          title={!sidebarOpen && !mobile ? (activeProduct?.label ?? "Products") : undefined}
        >
          {activeProduct?.icon ? (
            <span className={s.navProductIcon}>{activeProduct.icon}</span>
          ) : (
            <span className={s.navProductIcon}>●</span>
          )}
          {(sidebarOpen || mobile) && (
            <>
              <span className={s.navProductLabel}>{activeProduct?.label ?? "Select product"}</span>
              <span className={clsx(s.navProductChevron, productOpen && s.navProductChevronOpen)}>
                <ChevronDownIcon />
              </span>
            </>
          )}
        </button>

        {mobile && productInlineOpen && (
          <div className={s.navProductInlineList} role="listbox" aria-label={t("shared.nav.productSwitcher")}>
            {renderProductOptions(() => setProductInlineOpen(false))}
          </div>
        )}

        {!mobile && productDropdown.isOpen && (
          <div
            ref={productDropdownRef}
            className={s.navProductDropdown}
            style={{ ...productDropdown.getStyle(), minWidth: Math.max(currentWidth - 16, 200) }}
            role="listbox"
            aria-label={t("shared.nav.productSwitcher")}
          >
            {renderProductOptions(() => productDropdown.close())}
          </div>
        )}
      </div>
    );
  };

  const renderNavLinks = (sidebarOpen: boolean) => (
    <div className={s.navLinksScroll}>
      {links.map((item, i) => {
        if (isNavSection(item)) {
          return (
            <div key={i}>
              {sidebarOpen && <div className={s.navSectionLabel}>{item.label}</div>}
              {item.children.map((child, ci) => (
                <button
                  key={ci}
                  type="button"
                  className={clsx(s.navLink, child.active && s.navLinkActive, !sidebarOpen && s.navLinkIconOnly)}
                  onClick={wrapAction(child.onClick)}
                  title={!sidebarOpen ? child.label : undefined}
                >
                  <span className={clsx(s.navLinkIcon, child.active && s.navLinkIconActive)}>{child.icon}</span>
                  {sidebarOpen && (
                    <>
                      <span className={s.navLinkLabel}>{child.label}</span>
                      {child.badge != null && (
                        <span className={clsx(s.navLinkBadge, child.active && s.navLinkBadgeActive)}>
                          {child.badge}
                        </span>
                      )}
                    </>
                  )}
                </button>
              ))}
            </div>
          );
        }
        return (
          <button
            key={i}
            type="button"
            className={clsx(s.navLink, item.active && s.navLinkActive, !sidebarOpen && s.navLinkIconOnly)}
            onClick={wrapAction(item.onClick)}
            title={!sidebarOpen ? item.label : undefined}
          >
            <span className={clsx(s.navLinkIcon, item.active && s.navLinkIconActive)}>{item.icon}</span>
            {sidebarOpen && (
              <>
                <span className={s.navLinkLabel}>{item.label}</span>
                {item.badge != null && (
                  <span className={clsx(s.navLinkBadge, item.active && s.navLinkBadgeActive)}>{item.badge}</span>
                )}
              </>
            )}
          </button>
        );
      })}
    </div>
  );

  const renderSubLinks = (sidebarOpen: boolean) => {
    if (subLinks.length === 0) return null;
    return (
      <div className={s.navSubLinks}>
        {subLinks.map((sub, i) => (
          <button
            key={i}
            type="button"
            className={clsx(s.navSubLink, !sidebarOpen && s.navLinkIconOnly)}
            onClick={wrapAction(sub.onClick)}
            title={!sidebarOpen ? sub.label : undefined}
          >
            <span className={s.navLinkIcon}>{sub.icon}</span>
            {sidebarOpen && <span className={s.navLinkLabel}>{sub.label}</span>}
          </button>
        ))}
      </div>
    );
  };

  const renderOrgContextSummaryRow = (sidebarOpen: boolean) => {
    if (!orgContext || orgContext.selectedOrgs.length === 0) return null;
    return (
      <div className={orgStyles.contextBlock}>
        <OrgLogoStack orgs={orgContext.selectedOrgs} size="sm" />
        {sidebarOpen && (
          <OrgContextSummary orgs={orgContext.selectedOrgs} />
        )}
      </div>
    );
  };

  const renderOrgContext = (sidebarOpen: boolean) => {
    if (!showOrgContext || !orgContext) return null;

    if (orgContext.mode === "join") {
      return (
        <div className={orgStyles.navOrgContextSection}>
          {sidebarOpen ? (
            <Button
              type="button"
              color="primary"
              variant="solid"
              size="sm"
              fullWidth
              className={orgStyles.navOrgContextJoinButton}
              iconLeft={<UserPlus size={16} aria-hidden />}
              onClick={orgContext.onJoinOrganizationClick}
            >
              Join an organization
            </Button>
          ) : (
            <button
              type="button"
              className={clsx(
                orgStyles.navOrgContextTrigger,
                orgStyles.navOrgContextTriggerIconOnly,
              )}
              onClick={orgContext.onJoinOrganizationClick}
              title={t("shared.nav.joinOrganization")}
              aria-label={t("shared.nav.joinOrganization")}
            >
              <UserPlus size={16} strokeWidth={1.75} aria-hidden />
            </button>
          )}
        </div>
      );
    }

    const summary = renderOrgContextSummaryRow(sidebarOpen);
    if (!summary) return null;

    if (orgContext.mode === "single") {
      return (
        <div className={orgStyles.navOrgContextSection}>
          <div
            className={orgStyles.navOrgContextStatic}
            title={
              !sidebarOpen
                ? orgContext.selectedOrgs[0]?.name ?? "Organization"
                : undefined
            }
          >
            {summary}
          </div>
        </div>
      );
    }

    return (
      <div className={orgStyles.navOrgContextSection}>
        <button
          ref={orgContextTriggerRef}
          type="button"
          className={orgStyles.navOrgContextTrigger}
          onClick={handleOrgContextTrigger}
          aria-expanded={orgContextDropdown.isOpen}
          aria-haspopup="listbox"
          title={!sidebarOpen ? "Organization context" : undefined}
        >
          {summary}
          {sidebarOpen && (
            <span className={s.navUserChevron}>
              <ChevronDownIcon />
            </span>
          )}
        </button>
        {orgContextDropdown.isOpen && (
          <div
            ref={orgContextDropdownRef}
            className={orgStyles.dropdownPicker}
            style={{
              ...orgContextDropdown.getStyle(),
              ...(isMobileNav
                ? { left: 8, right: 8, width: "calc(100vw - 16px)", maxWidth: "none" }
                : { minWidth: Math.max(currentWidth - 16, 240) }),
            }}
            role="listbox"
            aria-multiselectable="true"
          >
            <OrgContextPickerBody
              availableOrgs={orgContext.availableOrgs}
              selectedOrgIds={orgContext.selectedOrgIds}
              isMobile={isMobileNav}
              onToggleOrg={orgContext.onToggleOrg}
              onSoloOrg={orgContext.onSoloOrg}
              onSelectAll={orgContext.onSelectAll}
            />
          </div>
        )}
      </div>
    );
  };

  const renderUserSection = (sidebarOpen: boolean) => {
    if (!user) return null;
    return (
      <div className={s.navUser}>
        <UserMenu
          ref={userMenuRef}
          user={user}
          sidebarOpen={sidebarOpen}
          dropdownDirection="up"
          dropdownMinWidth={Math.max(isMobileNav ? 200 : currentWidth - 16, 180)}
          onActionComplete={closeMobile}
          onOpenChange={(isUserMenuOpen) => {
            if (isUserMenuOpen) orgContextDropdown.close();
          }}
        />
      </div>
    );
  };

  if (isMobileNav) {
    return (
      <div className={s.navMobileRoot} role="navigation" aria-label={t("shared.nav.mainNavigation")}>
        <header className={s.navMobileHeader}>
          <button
            type="button"
            className={s.navMobileMenuBtn}
            onClick={toggleMobileMenu}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? t("shared.nav.closeNavigationMenu") : t("shared.nav.openNavigationMenu")}
          >
            <MobileMenuIcon open={mobileMenuOpen} />
          </button>
          {brand?.mark && <div className={s.navMobileLogoCenter}>{brand.mark}</div>}
          {headerTrailing && <div className={s.navMobileHeaderTrailing}>{headerTrailing}</div>}
        </header>

        {mobileMenuPresent && (
          <>
            <button
              type="button"
              className={clsx(s.navMobileBackdrop, mobileMenuEntered && s.navMobileBackdropEntered)}
              aria-label={t("shared.nav.closeNavigationMenu")}
              onClick={closeMobile}
            />
            <div className={clsx(s.navMobileDrawer, mobileMenuEntered && s.navMobileDrawerEntered)}>
              <div className={s.navMobileDrawerScroll}>
                {renderProductSection({ sidebarOpen: true, mobile: true })}
                <div className={s.navLinksArea}>{renderNavLinks(true)}</div>
              </div>
              <div className={s.navMobileDrawerFooter}>
                {renderSubLinks(true)}
                {renderOrgContext(true)}
                {renderUserSection(true)}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={embedded ? s.navOuterEmbedded : s.navOuter}
      style={{ width: currentWidth }}
      role="navigation"
      aria-label={t("shared.nav.mainNavigation")}
    >
      <div className={s.navBrand}>
        {open ? (
          <>
            {brand?.mark && <span className={s.navBrandMark}>{brand.mark}</span>}
            {brand?.name && <span className={s.navBrandName}>{brand.name}</span>}
            <button
              type="button"
              className={s.navCollapseBtn}
              onClick={() => onOpenChange?.(false)}
              aria-label={t("shared.nav.collapseSidebar")}
              title={t("shared.nav.collapse")}
            >
              <PanelLeftCloseIcon />
            </button>
          </>
        ) : (
          <div className={s.navBrandCollapsed}>
            <button
              type="button"
              className={s.navBrandExpandBtn}
              onClick={() => onOpenChange?.(true)}
              aria-label={t("shared.nav.expandSidebar")}
              title={t("shared.nav.expand")}
            >
              {brand?.mark ? <span className={s.navBrandMark}>{brand.mark}</span> : null}
            </button>
          </div>
        )}
      </div>

      {renderProductSection({ sidebarOpen: open })}

      <div className={s.navLinksArea}>{renderNavLinks(open)}</div>

      {renderSubLinks(open)}
      {renderOrgContext(open)}
      {renderUserSection(open)}
    </div>
  );
};
